import React, { useState } from "react";

import { motion, AnimatePresence } from "framer-motion";
import {
  Menu,
  X,
  Search,
  Compass,
  Home,
  Star,
  Zap,
  Film,
  Tv,
  Settings,
  LogIn,
  LogOut,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

import webPlayerStorage from "../utils/webPlayerStorage";

const MobileMenu = () => {
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const isAuthenticated = !!webPlayerStorage.getToken();

  const mobileMenuItems = [
    { icon: Search, label: "Search", id: "search", requiresAuth: false },
    {
      icon: Compass,
      label: "Discover",
      id: "discover",
      requiresAuth: false,
      guestOnly: true,
    },
    { icon: Home, label: "Home", id: "home", requiresAuth: false },
    { icon: Star, label: "Favorites", id: "watchlist", requiresAuth: true },
    { icon: Zap, label: "Live TV", id: "live", requiresAuth: true },
    { icon: Film, label: "Movies", id: "movies", requiresAuth: false },
    { icon: Tv, label: "Series", id: "series", requiresAuth: false },
    { icon: Settings, label: "Settings", id: "settings", requiresAuth: true },
  ];

  const handleMobileMenuClick = (menuId) => {
    setIsMobileMenuOpen(false);
    switch (menuId) {
      case "search":
        navigate("/web-player/search");
        break;
      case "discover":
        navigate("/web-player/discover");
        break;
      case "home":
        navigate("/web-player/home");
        break;
      case "watchlist":
        navigate("/web-player/watchlist");
        break;
      case "live":
        navigate("/web-player/live-tv");
        break;
      case "movies":
        navigate("/web-player/movies");
        break;
      case "series":
        navigate("/web-player/series");
        break;
      case "settings":
        navigate("/web-player/settings");
        break;
      case "login":
        navigate("/web-player/login");
        break;
      case "logout":
        setShowLogoutConfirm(true);
        break;
      default:
        break;
    }
  };

  const handleLogoutConfirm = () => {
    setShowLogoutConfirm(false);
    setIsMobileMenuOpen(false);
    webPlayerStorage.clearAll();
    navigate("/web-player/home");
    window.location.reload();
  };

  return (
    <>
      {/* Mobile Hamburger Menu Button */}
      <button
        className="fixed top-4 left-4 z-[200] hidden p-3 rounded-full transition-all duration-300 hover:scale-110 bg-black/50 text-white hover:bg-black/70"
        onClick={() => setIsMobileMenuOpen(true)}
      >
        <Menu className="w-5 h-5" />
      </button>

      {/* Mobile Menu - iOS Bubble Effect */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            animate={{
              clipPath: "circle(150% at 40px 40px)",
            }}
            className="fixed inset-0 z-[300] lg:hidden flex flex-col bg-black/98 backdrop-blur-xl"
            exit={{
              clipPath: "circle(0% at 40px 40px)",
            }}
            initial={{
              clipPath: "circle(0% at 40px 40px)",
            }}
            transition={{
              type: "spring",
              stiffness: 300,
              damping: 40,
            }}
          >
            {/* Close Button */}
            <button
              className="absolute top-4 left-4 z-50 p-3 rounded-full transition-all duration-300 hover:scale-110 bg-gray-800 text-white hover:bg-gray-700"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <X className="w-5 h-5" />
            </button>

            {/* Menu Content */}
            <div className="flex flex-col h-full pt-20 landscape:pt-12 pb-8 landscape:pb-2 px-6 landscape:px-3">
              {/* Logo and Title */}
              <motion.div
                animate={{ opacity: 1, y: 0 }}
                className="flex items-center gap-4 landscape:gap-2 mb-8 landscape:mb-3"
                initial={{ opacity: 0, y: -20 }}
                transition={{ delay: 0.1 }}
              >
                <img
                  alt="Lunova"
                  className="w-12 h-12 landscape:w-8 landscape:h-8 rounded-xl"
                  src="/logo.png"
                />
                <span className="text-2xl landscape:text-base font-bold text-white">
                  Lunova
                </span>
              </motion.div>

              {/* Menu Items */}
              <div className="flex-1 space-y-2 landscape:space-y-0.5 overflow-y-auto">
                {mobileMenuItems
                  .filter(
                    (item) =>
                      (!item.requiresAuth || isAuthenticated) &&
                      (!item.guestOnly || !isAuthenticated)
                  )
                  .map((item, index) => (
                    <motion.button
                      animate={{ opacity: 1, x: 0 }}
                      className="w-full flex items-center gap-4 landscape:gap-2 px-4 landscape:px-2 py-4 landscape:py-1.5 rounded-xl transition-colors text-white hover:bg-white/10"
                      initial={{ opacity: 0, x: -30 }}
                      key={item.id}
                      transition={{ delay: 0.05 * (index + 1) }}
                      onClick={() => handleMobileMenuClick(item.id)}
                    >
                      <div className="w-10 h-10 landscape:w-6 landscape:h-6 rounded-full flex items-center justify-center bg-white/10">
                        <item.icon className="w-5 h-5 landscape:w-3.5 landscape:h-3.5" />
                      </div>
                      <span className="text-lg landscape:text-sm font-medium">
                        {item.label}
                      </span>
                    </motion.button>
                  ))}
              </div>

              {/* Login/Logout Button */}
              <motion.div
                animate={{ opacity: 1, y: 0 }}
                className="pt-4 landscape:pt-1 border-t border-gray-700/20"
                initial={{ opacity: 0, y: 20 }}
                transition={{ delay: 0.3 }}
              >
                {isAuthenticated ? (
                  <button
                    className="w-full flex items-center gap-4 landscape:gap-2 px-4 landscape:px-2 py-4 landscape:py-1.5 text-red-500 hover:bg-red-500/10 rounded-xl transition-colors"
                    onClick={() => handleMobileMenuClick("logout")}
                  >
                    <div className="w-10 h-10 landscape:w-6 landscape:h-6 rounded-full flex items-center justify-center bg-red-500/10">
                      <LogOut className="w-5 h-5 landscape:w-3.5 landscape:h-3.5" />
                    </div>
                    <span className="text-lg landscape:text-sm font-medium">
                      Logout
                    </span>
                  </button>
                ) : (
                  <button
                    className="w-full flex items-center gap-4 landscape:gap-2 px-4 landscape:px-2 py-4 landscape:py-1.5 rounded-xl transition-colors text-blue-400 hover:bg-blue-500/10"
                    onClick={() => handleMobileMenuClick("login")}
                  >
                    <div className="w-10 h-10 landscape:w-6 landscape:h-6 rounded-full flex items-center justify-center bg-blue-500/10">
                      <LogIn className="w-5 h-5 landscape:w-3.5 landscape:h-3.5" />
                    </div>
                    <span className="text-lg landscape:text-sm font-medium">
                      Login
                    </span>
                  </button>
                )}
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Logout Confirmation Dialog */}
      <AnimatePresence>
        {showLogoutConfirm && (
          <motion.div
            animate={{ opacity: 1 }}
            className="fixed inset-0 z-[400] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
            exit={{ opacity: 0 }}
            initial={{ opacity: 0 }}
            onClick={() => setShowLogoutConfirm(false)}
          >
            <motion.div
              animate={{ scale: 1, opacity: 1 }}
              className="bg-gray-900 rounded-2xl p-6 max-w-sm w-full"
              exit={{ scale: 0.9, opacity: 0 }}
              initial={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-xl font-bold text-white mb-2 text-center">
                Log Out
              </h3>
              <p className="text-gray-400 text-center mb-6">
                Are you sure you want to log out?
              </p>
              <div className="flex gap-3">
                <button
                  className="flex-1 px-4 py-3 rounded-xl bg-gray-700 text-white font-medium hover:bg-gray-600 transition-colors"
                  onClick={() => setShowLogoutConfirm(false)}
                >
                  Cancel
                </button>
                <button
                  className="flex-1 px-4 py-3 rounded-xl bg-red-600 text-white font-medium hover:bg-red-500 transition-colors"
                  onClick={handleLogoutConfirm}
                >
                  Yes, Log Out
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default MobileMenu;
